import * as React from "react";
import URI from "urijs";
import i18n from "i18n";
import { useTranslation } from "react-i18next";
import Routes from "routing/Routes";
import { Button } from "components/bootstrap";
import { LinkContainer, ReadOnlyFormGroup, Spinner } from "components/common";
import SectionComponent from "components/common/Section/SectionComponent";
import useRoles from "common/hooks/useRoles";
import useSAMLBackendDetails from "authentication/components/saml/hooks/useSAMLBackendDetails";
import SAMLCertInfoTable from "authentication/components/saml/config/SAMLCertInfoTable";
import { SSO_SAML_ACS_PATH, SSO_SAML_LOGOUT_PATH } from "authentication/components/saml/constants";
const normalizeURI = (url) => new URI(url).normalizePath().toString();
const ConfigValue = ({ label, value, code = false }) => value && /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label, value: code ? /* @__PURE__ */ React.createElement("code", null, value) : value });
const certDetails = (isLoading, certs) => {
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(
      Spinner,
      {
        text: i18n.t("saml.saml_backend_config_section.loading_certificate_details", {
          ns: "enterprise_authentication"
        })
      }
    );
  }
  if (!certs) {
    return /* @__PURE__ */ React.createElement("em", null, i18n.t("saml.saml_backend_config_section.certificate_details_error", { ns: "enterprise_authentication" }));
  }
  return Object.keys(certs || {}).map((key) => {
    const { certificate, subject, issuer, sha1_fingerprint, sha256_fingerprint, valid_from, valid_to } = certs[key];
    return /* @__PURE__ */ React.createElement(
      SAMLCertInfoTable,
      {
        key: sha256_fingerprint,
        certificate,
        subject,
        issuer,
        sha256Fingerprint: sha256_fingerprint,
        sha1Fingerprint: sha1_fingerprint,
        validFrom: valid_from,
        validTo: valid_to
      }
    );
  });
};
const SAMLBackendConfigSection = ({ authenticationBackend }) => {
  const { t } = useTranslation("enterprise_authentication");
  const { isLoading, roles } = useRoles();
  const {
    id,
    title,
    description,
    defaultRoles,
    defaultUserTimezone,
    idpMetadataUrl,
    idpMetadataRefreshAtLogin,
    idpSsoServicePostBindingUrl,
    idpSsoServiceRedirectBindingUrl,
    idpLogoutServicePostBindingUrl,
    idpLogoutServiceRedirectBindingUrl,
    idpEntityId,
    idpUserNameAttribute,
    idpEmailAttribute,
    idpFirstNameAttribute,
    idpLastNameAttribute,
    idpFullNameAttribute,
    spBaseUrl,
    spEntityId
  } = authenticationBackend;
  const { isLoading: areDetailsLoading, details } = useSAMLBackendDetails(id);
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const foundRoles = defaultRoles.split(",").map((roleId) => {
    var _a;
    return (_a = roles.find((role) => role.id === roleId)) == null ? void 0 : _a.name;
  }).join(", ");
  const idpCertificates = certDetails(areDetailsLoading, details == null ? void 0 : details.idp_certs);
  const spCertificates = certDetails(areDetailsLoading, details == null ? void 0 : details.sp_certs);
  return /* @__PURE__ */ React.createElement(
    SectionComponent,
    {
      title: t("saml.saml_backend_config_section.title"),
      headerActions: /* @__PURE__ */ React.createElement(LinkContainer, { to: Routes.SYSTEM.AUTHENTICATION.BACKENDS.edit(id, "idp_config") }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", bsSize: "small" }, t("saml.saml_backend_config_section.edit")))
    },
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: t("saml.saml_backend_config_section.title_label"), value: title }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: t("saml.saml_backend_config_section.description"), value: description }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: t("saml.saml_backend_config_section.default_roles"), value: foundRoles }),
    /* @__PURE__ */ React.createElement(
      ReadOnlyFormGroup,
      {
        label: t("saml.saml_backend_config_section.default_user_time_zone"),
        value: defaultUserTimezone || t("saml.saml_backend_config_section.browser_time_zone")
      }
    ),
    /* @__PURE__ */ React.createElement("hr", null),
    /* @__PURE__ */ React.createElement("h3", null, t("saml.saml_backend_config_section.identity_provider")),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.metadata_url"), value: idpMetadataUrl }),
    /* @__PURE__ */ React.createElement(
      ReadOnlyFormGroup,
      {
        label: t("saml.saml_backend_config_section.metadata_refresh_at_login"),
        value: idpMetadataRefreshAtLogin
      }
    ),
    /* @__PURE__ */ React.createElement(
      ConfigValue,
      {
        label: t("saml.saml_backend_config_section.sso_service_redirect_url"),
        value: idpSsoServiceRedirectBindingUrl
      }
    ),
    /* @__PURE__ */ React.createElement(
      ConfigValue,
      {
        label: t("saml.saml_backend_config_section.sso_service_post_url"),
        value: idpSsoServicePostBindingUrl
      }
    ),
    /* @__PURE__ */ React.createElement(
      ConfigValue,
      {
        label: t("saml.saml_backend_config_section.logout_service_redirect_url"),
        value: idpLogoutServiceRedirectBindingUrl
      }
    ),
    /* @__PURE__ */ React.createElement(
      ConfigValue,
      {
        label: t("saml.saml_backend_config_section.logout_service_post_url"),
        value: idpLogoutServicePostBindingUrl
      }
    ),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.entity_id"), value: idpEntityId }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: t("saml.saml_backend_config_section.certificate"), value: idpCertificates }),
    /* @__PURE__ */ React.createElement("hr", null),
    /* @__PURE__ */ React.createElement("h3", null, t("saml.saml_backend_config_section.service_provider")),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.base_url"), value: spBaseUrl }),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.entity_id"), value: spEntityId }),
    /* @__PURE__ */ React.createElement(
      ConfigValue,
      {
        label: t("saml.saml_backend_config_section.acs_url"),
        value: normalizeURI(`${spBaseUrl}/${SSO_SAML_ACS_PATH}`)
      }
    ),
    /* @__PURE__ */ React.createElement(
      ConfigValue,
      {
        label: t("saml.saml_backend_config_section.single_logout_service_url"),
        value: normalizeURI(`${spBaseUrl}/${SSO_SAML_LOGOUT_PATH}`)
      }
    ),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: t("saml.saml_backend_config_section.certificate"), value: spCertificates }),
    [
      idpUserNameAttribute,
      idpEmailAttribute,
      idpFirstNameAttribute,
      idpLastNameAttribute,
      idpFullNameAttribute
    ].some((value) => !!value) && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement("h3", null, t("saml.saml_backend_config_section.user_attribute_mapping")), /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.user_name"), value: idpUserNameAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.email"), value: idpEmailAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.first_name"), value: idpFirstNameAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.last_name"), value: idpLastNameAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: t("saml.saml_backend_config_section.full_name"), value: idpFullNameAttribute }))
  );
};
export default SAMLBackendConfigSection;
